import { DateTime } from 'luxon';
import { Netmask } from 'netmask';
import { defineStore } from 'pinia';
import { computed, readonly, ref } from 'vue';

const VPN_TIMEOUT_MS = 4000;
const NATE_EU_VPN_BASE_URL = 'http://10.212.212.1';
const NATE_US_VPN_BASE_URL = 'http://10.213.213.1';
const LATEST_HANDSHAKE_ONLINE_THRESHOLD_SECONDS = 5 * 60;

const useVpnStore = defineStore('vpn', () => {
  const region = ref(undefined);
  const clients = ref([]);

  const connected = computed(() => (region.value === undefined ? undefined : !!region.value));
  const onlineClients = computed(() => {
    return clients.value.filter((client) => {
      if (!client.latestHandshakeAt) return false;

      const latestHandshakeAt = DateTime.fromISO(client.latestHandshakeAt);
      return latestHandshakeAt >= DateTime.now().minus({ seconds: LATEST_HANDSHAKE_ONLINE_THRESHOLD_SECONDS });
    });
  });

  const clientsEndpoint = computed(() => {
    if (region.value === 'EU') return `${NATE_EU_VPN_BASE_URL}/vpn/clients`;
    if (region.value === 'US') return `${NATE_US_VPN_BASE_URL}/vpn/clients`;

    return null;
  });

  function stationOnline({ host }) {
    return !!onlineClients.value.find((client) => {
      const networks =
        client.serverAllowedIPs?.map((address) => {
          return new Netmask(address);
        }) ?? [];

      return !!networks.find((network) => network.contains(host));
    });
  }

  async function refresh() {
    region.value = await Promise.any([
      new Promise((resolve, reject) =>
        fetch(NATE_EU_VPN_BASE_URL)
          .then(() => resolve('EU'))
          .catch(() => reject('EU')),
      ),
      new Promise((resolve, reject) =>
        fetch(NATE_US_VPN_BASE_URL)
          .then(() => resolve('US'))
          .catch(() => reject('US')),
      ),
      new Promise((resolve) => setTimeout(() => resolve(null), VPN_TIMEOUT_MS)),
    ]);

    await refreshClients();

    return region.value;
  }

  async function refreshClients() {
    if (!clientsEndpoint.value) {
      clients.value = [];
      return;
    }

    const response = await fetch(clientsEndpoint.value);
    const data = await response.json();

    if (!data.success) {
      console.warn('VPN clients refresh failed:', data);
      clients.value = [];
      return;
    }

    clients.value = data.clients;
  }

  return {
    region: readonly(region),

    connected,
    onlineClients,

    stationOnline,
    refresh,
  };
});

export default useVpnStore;
